import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import {
  useComprasHistorico,
  useComprasHistoricoOwner,
} from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, DollarSign, ShoppingCart, Building2, Users } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { METODO_LABELS } from "@/lib/constants";
import { useModulos } from "@/hooks/use-modulos";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { filtrarPorParceiro, parceirosDasCompras, statusCompra } from "@/lib/compras-vendas";

const TIPO_VEICULO_LABELS: Record<string, { label: string; emoji: string }> = {
  auto_leve: { label: "Automóvel", emoji: "🚗" },
  moto: { label: "Motocicleta", emoji: "🏍️" },
  caminhao: { label: "Caminhão", emoji: "🚛" },
  onibus: { label: "Ônibus", emoji: "🚌" },
};

function ComprasTable({ compras }: { compras: any[] }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Usuário</TableHead>
            <TableHead>Tipo de Veículo</TableHead>
            <TableHead className="text-right">Créditos</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead>Método</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {compras.map((compra) => {
            const tipoInfo = TIPO_VEICULO_LABELS[compra.tipo_veiculo] || {
              label: compra.tipo_veiculo,
              emoji: "🚗",
            };
            return (
              <TableRow key={compra.id}>
                <TableCell className="text-sm font-medium">
                  {format(new Date(compra.data), "dd MMM yyyy HH:mm", {
                    locale: ptBR,
                  })}
                </TableCell>
                <TableCell className="text-sm">{compra.user_nome}</TableCell>
                <TableCell className="text-sm">
                  {tipoInfo.emoji} {tipoInfo.label}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {compra.quantidade}
                </TableCell>
                <TableCell className="text-right font-medium">
                  R${" "}
                  {compra.valor_total.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {METODO_LABELS[compra.metodo] || compra.metodo}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={statusCompra(compra.status).variant}>
                    {statusCompra(compra.status).label}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function FiltroParceiro({
  compras,
  value,
  onChange,
}: {
  compras: { empresa_id: string; empresa_nome: string }[] | undefined;
  value: string;
  onChange: (v: string) => void;
}) {
  const parceiros = parceirosDasCompras(compras);
  if (parceiros.length === 0) return null;
  return (
    <div className="flex items-center gap-2">
      <Users className="w-4 h-4 text-muted-foreground" />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full sm:w-[260px]" data-testid="select-filtro-parceiro">
          <SelectValue placeholder="Todos os parceiros" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos os parceiros</SelectItem>
          {parceiros.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.nome}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function OwnerView() {
  const { data: todasCompras, isLoading, error } = useComprasHistoricoOwner();
  const [parceiroId, setParceiroId] = useState("all");
  const compras = filtrarPorParceiro(todasCompras, parceiroId);

  const stats = {
    totalVendido:
      compras?.reduce(
        (sum, c) => sum + (c.status === "pago" ? c.valor_total : 0),
        0,
      ) || 0,
    creditosVendidos:
      compras?.reduce(
        (sum, c) => sum + (c.status === "pago" ? c.quantidade : 0),
        0,
      ) || 0,
    numeroVendas: compras?.filter((c) => c.status === "pago").length || 0,
    vendasPendentes:
      compras?.filter((c) => c.status === "processando" || c.status === "pendente").length || 0,
  };

  // Group by vistoriadora → parceiro
  const byVistoriadora = new Map<
    string,
    {
      vistoriadora_nome: string;
      parceiros: Map<string, { empresa_nome: string; compras: typeof compras }>;
    }
  >();

  (compras || []).forEach((compra) => {
    const vId = compra.vistoriadora_id || "__sem_vistoriadora__";
    const vNome = compra.vistoriadora_nome;

    if (!byVistoriadora.has(vId)) {
      byVistoriadora.set(vId, { vistoriadora_nome: vNome, parceiros: new Map() });
    }
    const vEntry = byVistoriadora.get(vId)!;

    const pId = compra.empresa_id;
    const pNome = compra.empresa_nome;
    if (!vEntry.parceiros.has(pId)) {
      vEntry.parceiros.set(pId, { empresa_nome: pNome, compras: [] });
    }
    vEntry.parceiros.get(pId)!.compras!.push(compra);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Dashboard de Vendas
        </h1>
        <p className="text-muted-foreground mt-2">
          Visão geral de todas as vistoriadoras e seus parceiros
        </p>
      </div>
      <FiltroParceiro compras={todasCompras} value={parceiroId} onChange={setParceiroId} />

      {/* Estatísticas globais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <>
            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Total Vendido</p>
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold">
                    R${" "}
                    {stats.totalVendido.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Créditos Vendidos</p>
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold">
                    {stats.creditosVendidos.toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Número de Vendas</p>
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold">{stats.numeroVendas}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Vendas Pendentes</p>
                    <ShoppingCart className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold">{stats.vendasPendentes}</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Agrupado por vistoriadora → parceiro */}
      {error ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-destructive font-medium">Erro ao carregar vendas</p>
              <p className="text-muted-foreground text-sm mt-1">{String(error)}</p>
            </div>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : byVistoriadora.size === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-muted-foreground">Nenhuma venda registrada</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        Array.from(byVistoriadora.entries()).map(
          ([vId, { vistoriadora_nome, parceiros }]) => {
            const totalVistoriadora = Array.from(parceiros.values()).reduce(
              (sum, p) =>
                sum +
                (p.compras || []).reduce(
                  (s, c) => s + (c.status === "pago" ? c.valor_total : 0),
                  0,
                ),
              0,
            );
            const creditosVistoriadora = Array.from(parceiros.values()).reduce(
              (sum, p) =>
                sum +
                (p.compras || []).reduce(
                  (s, c) => s + (c.status === "pago" ? c.quantidade : 0),
                  0,
                ),
              0,
            );

            return (
              <div key={vId} className="space-y-3">
                {/* Cabeçalho da Vistoriadora */}
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-bold">{vistoriadora_nome}</h2>
                  <div className="flex gap-2 ml-auto text-sm text-muted-foreground">
                    <span>
                      R${" "}
                      {totalVistoriadora.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                    <span>·</span>
                    <span>{creditosVistoriadora} créditos</span>
                  </div>
                </div>

                {/* Parceiros desta vistoriadora */}
                {Array.from(parceiros.entries()).map(
                  ([pId, { empresa_nome, compras: comprasParceiro }]) => {
                    const totalParceiro = (comprasParceiro || []).reduce(
                      (s, c) => s + (c.status === "pago" ? c.valor_total : 0),
                      0,
                    );
                    const creditosParceiro = (comprasParceiro || []).reduce(
                      (s, c) => s + (c.status === "pago" ? c.quantidade : 0),
                      0,
                    );

                    return (
                      <Card key={pId} className="ml-4">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-base">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            {empresa_nome}
                            <div className="flex gap-2 ml-auto text-sm font-normal text-muted-foreground">
                              <span>
                                R${" "}
                                {totalParceiro.toLocaleString("pt-BR", {
                                  minimumFractionDigits: 2,
                                })}
                              </span>
                              <span>·</span>
                              <span>{creditosParceiro} créditos</span>
                              <span>·</span>
                              <span>{(comprasParceiro || []).length} venda(s)</span>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ComprasTable compras={comprasParceiro || []} />
                        </CardContent>
                      </Card>
                    );
                  },
                )}
              </div>
            );
          },
        )
      )}
    </div>
  );
}

function VistoriadoraView({ empresaId }: { empresaId: string }) {
  const { data: todasCompras, isLoading: comprasLoading } =
    useComprasHistorico(empresaId);
  const [parceiroId, setParceiroId] = useState("all");
  const compras = filtrarPorParceiro(todasCompras, parceiroId);

  const stats = {
    totalVendido:
      compras?.reduce(
        (sum, c) => sum + (c.status === "pago" ? c.valor_total : 0),
        0,
      ) || 0,
    creditosVendidos:
      compras?.reduce(
        (sum, c) => sum + (c.status === "pago" ? c.quantidade : 0),
        0,
      ) || 0,
    numeroVendas: compras?.filter((c) => c.status === "pago").length || 0,
    vendaPendentes:
      compras?.filter((c) => c.status === "processando" || c.status === "pendente").length || 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Dashboard de Vendas
        </h1>
        <p className="text-muted-foreground mt-2">
          Acompanhe o histórico de vendas de créditos aos seus parceiros
        </p>
      </div>
      <FiltroParceiro compras={todasCompras} value={parceiroId} onChange={setParceiroId} />

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {comprasLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <>
            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Total Vendido</p>
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold">
                    R${" "}
                    {stats.totalVendido.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Créditos Vendidos
                    </p>
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold">
                    {stats.creditosVendidos.toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Número de Vendas
                    </p>
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold">{stats.numeroVendas}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Vendas Pendentes
                    </p>
                    <ShoppingCart className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold">{stats.vendaPendentes}</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Histórico de Vendas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Histórico de Vendas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {comprasLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : compras && compras.length > 0 ? (
            <ComprasTable compras={compras} />
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Nenhuma venda registrada</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function DashboardVendas() {
  const { profile } = useAuth();
  const { modulos, isLoading: modulosLoading } = useModulos();

  const isOwner = profile?.empresa?.tipo === "OWNER";
  const empresaId = profile?.empresaId;

  if (!modulosLoading && !modulos.creditosPrePagos) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <TrendingUp className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Módulo não disponível</h2>
          <p className="text-muted-foreground max-w-sm">
            O módulo de Créditos Pré-Pagos não está ativo para sua vistoriadora.
          </p>
        </div>
      </Shell>
    );
  }

  if (!isOwner && !empresaId) {
    return (
      <Shell>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Empresa não identificada.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {isOwner ? (
        <OwnerView />
      ) : (
        <VistoriadoraView empresaId={empresaId!} />
      )}
    </Shell>
  );
}
